"use client";

import React, { useCallback, useMemo, useRef, useState } from "react";
import NextLink from "next/link";
import { useParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import * as Sentry from "@sentry/nextjs";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { useQueryClient } from "@tanstack/react-query";
import { Header } from "@components/header";
import SetWall from "src/components/art/SetWall";
import SetMaterialPicker from "src/components/art/SetMaterialPicker";
import { useSetCatalogue } from "src/components/art/useArtSets";
import { artworkImageUrl } from "src/components/art/useArtwork";
import {
  artworkPath,
  commonSizes,
  defaultSetSize,
  findSet,
  resolveSet,
  setMaterialOptions,
  setPricedVariant,
  setSizeRange,
  setTotal,
} from "src/components/art/art-sets";
import { artworkAspect, localized, type Artwork } from "src/components/art/artwork-types";
import type { CanvasVariant } from "src/components/canvas/variant-resolver";
import FinishPicker from "src/components/canvas/FinishPicker";
import SizePicker from "src/components/canvas/SizePicker";
import TrustStrip from "src/components/canvas/TrustStrip";
import { OptionDensityContext, STEP_COUNTER } from "src/components/canvas/OptionCard";
import { trustPointKeys } from "src/components/canvas/trust-points";
import {
  changeFinish,
  closestBySize,
  finishOf,
  variantsOfFinish,
  type FinishAxis,
} from "src/components/canvas/finish-options";
import { sizeLabel } from "src/components/canvas/size-groups";
import { formatMoney, variantDisplayPrice } from "src/components/canvas/variant-price";
import { MobileContinueBar } from "src/components/canvas/MobileContinueBar";
import { useCheckoutRegistration } from "src/hooks/useCheckoutRegistration";
import { useOrder } from "src/contexts";
import { orderQueryKey } from "src/hooks/useOrder";
import { useCartDrawer } from "src/contexts/cart-drawer-context";
import { orderApi } from "src/services/api";
import { useShopCurrencyValue } from "src/hooks/useShopMarket";

/**
 * A partly added set: which pieces made it into the cart and at what size.
 *
 * Kept until every piece is in, or the customer dismisses it. While it stands
 * the size is held at the one already in the cart, so "add the missing works"
 * completes the same set rather than starting a second one in another size.
 */
interface PartialAdd {
  variantId: string;
  added: string[];
  failed: Array<{ slug: string; message: string | null }>;
}

const errorMessage = (err: unknown): string | null => {
  const data = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (Array.isArray(data)) return data.filter(Boolean).join(", ") || null;
  return typeof data === "string" && data ? data : null;
};

export interface ArtSetDetailProps {
  setSlug: string;
}

/**
 * One set: the works hung together, one finish and one size for all of them,
 * and a single button that puts each in the cart as its own line.
 *
 * The pickers are the artwork page's own, fed only the sizes every piece can
 * print. Prices are the pieces' normal prices added up; the size tiles show
 * that total so the number under a size is the number paid.
 */
export default function ArtSetDetail({ setSlug }: ArtSetDetailProps) {
  const t = useTranslations("art");
  const tEditor = useTranslations("editor");
  const te = tEditor as unknown as (key: string) => string;
  const locale = useLocale();
  const params = useParams();
  const localeSegment = (params?.locale as string) ?? locale;
  const optionsRef = useRef<HTMLHRElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"), { noSsr: true });
  const currency = useShopCurrencyValue();

  const set = findSet(setSlug);
  const { catalogue, variants, axes, isLoading, isError } = useSetCatalogue();

  const resolved = useMemo(
    () => (set ? resolveSet(set, catalogue) : { pieces: [], missing: [] }),
    [set, catalogue],
  );
  const pieces = resolved.pieces;
  const complete = !!set && resolved.missing.length === 0;
  const sizes = useMemo(
    () => (complete ? commonSizes(pieces, variants) : []),
    [complete, pieces, variants],
  );
  const sizeVariants = useMemo(() => sizes.map((s) => s.variant), [sizes]);

  const { ensureOrderExists } = useOrder();
  const { openCartDrawer } = useCartDrawer();
  const queryClient = useQueryClient();
  const [checkoutTarget, setCheckoutTarget] = useState<
    { orderId: string; guestToken: string } | null
  >(null);
  useCheckoutRegistration(checkoutTarget);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adding, setAdding] = useState<{ current: number; total: number } | null>(null);
  const [partial, setPartial] = useState<PartialAdd | null>(null);

  const fallback = useMemo(() => defaultSetSize(sizes, axes), [sizes, axes]);
  const wantedId = partial?.variantId ?? selectedId;
  const selected = sizes.find((s) => s.variant.id === wantedId) ?? fallback;
  const finish = useMemo(() => finishOf(selected?.variant), [selected]);

  const materialOptions = useMemo(
    () => setMaterialOptions(sizes, pieces.length, currency),
    [sizes, pieces.length, currency],
  );
  // Mount and surface are asked within the chosen material; the material
  // itself is asked above, with prices, by SetMaterialPicker.
  const finishVariants = useMemo(
    () => sizeVariants.filter((v) => v.axisValues?.material === finish.material),
    [sizeVariants, finish.material],
  );
  const range = useMemo(() => setSizeRange(sizes), [sizes]);

  const handleFinishChange = useCallback(
    (key: FinishAxis, value: string) => {
      if (partial) return;
      const next = changeFinish(sizeVariants, axes, finish, key, value);
      const match = closestBySize(variantsOfFinish(sizeVariants, next), selected?.variant);
      if (match) setSelectedId(match.id);
    },
    [partial, sizeVariants, axes, finish, selected],
  );

  // The size tiles, each priced as the whole set.
  const groups = useMemo(() => {
    const list = variantsOfFinish(sizeVariants, finish)
      .map((v) => setPricedVariant(v, pieces.length))
      .sort(
        (a, b) =>
          a.printSpec.widthMm * a.printSpec.heightMm - b.printSpec.widthMm * b.printSpec.heightMm,
      );
    return list.length ? [{ ratio: "", variants: list }] : [];
  }, [sizeVariants, finish, pieces.length]);

  const total = selected
    ? setTotal(
        pieces.map(() => selected.variant),
        currency,
      )
    : null;
  const piecePrice = selected ? variantDisplayPrice(selected.variant, currency) : null;

  /**
   * Each piece goes in as its own line, one after another, through the same
   * call the artwork page makes. Sequential so the cart's order matches the
   * wall and no two writes race on the same order.
   *
   * A failure does not stop the rest: the customer is told exactly which
   * pieces are in the cart and which are not, and offered the missing ones
   * again. A set is never left half-added without saying so.
   */
  const addPieces = useCallback(
    async (slugs: string[], variantId: string, alreadyAdded: string[]) => {
      setAdding({ current: 1, total: slugs.length });
      const added = [...alreadyAdded];
      const failed: PartialAdd["failed"] = [];
      let target: { orderId: string; guestToken: string } | null = null;
      try {
        target = await ensureOrderExists();
      } catch (err) {
        Sentry.captureException(err);
        const message = errorMessage(err);
        slugs.forEach((slug) => failed.push({ slug, message }));
      }
      if (target) {
        for (let i = 0; i < slugs.length; i++) {
          setAdding({ current: i + 1, total: slugs.length });
          try {
            await orderApi.orderControllerAddArtwork(
              target.orderId,
              { slug: slugs[i], variantId, quantity: 1 },
              target.guestToken,
            );
            added.push(slugs[i]);
          } catch (err) {
            Sentry.captureException(err);
            failed.push({ slug: slugs[i], message: errorMessage(err) });
          }
        }
        // The drawer renders the order query cache; refresh it so what was
        // added is what the customer sees (see ArtworkDetail).
        await queryClient.invalidateQueries({ queryKey: orderQueryKey(target.orderId) });
        setCheckoutTarget(target);
      }
      setAdding(null);
      if (failed.length) {
        setPartial({ variantId, added, failed });
      } else {
        setPartial(null);
        openCartDrawer();
      }
    },
    [ensureOrderExists, openCartDrawer, queryClient],
  );

  const handleAdd = () => {
    if (!selected || adding) return;
    void addPieces(
      pieces.map((p) => p.slug),
      selected.variant.id,
      [],
    );
  };

  const handleRetry = () => {
    if (!partial || adding) return;
    void addPieces(
      partial.failed.map((f) => f.slug),
      partial.variantId,
      partial.added,
    );
  };

  const titleOf = (slug: string) => {
    const piece = pieces.find((p) => p.slug === slug);
    return piece ? `${localized(piece.title, locale)} (${piece.artist})` : slug;
  };

  if (!set) {
    return (
      <>
        <Header sticky={false} />
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Alert severity="error">{t("sets.notFound")}</Alert>
        </Container>
      </>
    );
  }

  const title = localized(set.title, locale);
  const intro = localized(set.intro, locale);
  const root = `/${localeSegment}/art`;
  const material = selected?.variant.axisValues?.material;
  const mount = selected?.variant.axisValues?.mount;
  const orientation =
    pieces[0] && artworkAspect(pieces[0]) < 1 ? ("portrait" as const) : ("landscape" as const);
  const loading = isLoading && !pieces.length;
  const unavailable = !loading && (!complete || (variants.length > 0 && sizes.length === 0) || isError);

  return (
    <>
      <Header sticky={false} />

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Button component={NextLink} href={root} sx={{ mb: 2, pl: 0 }}>
          ← {t("backToGallery")}
        </Button>

        <Grid container spacing={{ xs: 3, md: 5 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            {loading || !selected ? (
              <Box
                sx={{
                  height: { xs: 300, md: 480 },
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {loading ? <CircularProgress /> : null}
              </Box>
            ) : (
              <SetWall
                initialScene={set.roomScene}
                pieces={pieces.map((p, i) => ({
                  src: artworkImageUrl(p.slug, "display", p.updatedAt),
                  alt: `${localized(p.title, locale)} — ${p.artist}`,
                  widthMm: selected.pieces[i].widthMm,
                  heightMm: selected.pieces[i].heightMm,
                }))}
                captions={{ sofa: t("trueScale"), bed: t("trueScaleBed"), plain: t("plainView") }}
                sceneLabels={{ sofa: t("sceneSofa"), bed: t("sceneBed"), plain: t("scenePlain") }}
              />
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ letterSpacing: ".1em", textTransform: "uppercase" }}
            >
              {t("sets.kicker")} · {t("sets.pieceCount", { count: set.artworkSlugs.length })}
            </Typography>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mt: 0.5 }}>
              {title}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.65 }}>
              {intro}
            </Typography>
            {range.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                {range.length === 1
                  ? t("sets.sizeOne", { size: sizeLabel(range[0].widthMm, range[0].heightMm) })
                  : t("sets.sizeRange", {
                      min: sizeLabel(range[0].widthMm, range[0].heightMm),
                      max: sizeLabel(range[range.length - 1].widthMm, range[range.length - 1].heightMm),
                    })}
              </Typography>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : unavailable || !selected ? (
              <Alert severity="info" sx={{ mt: 3 }}>
                {t("sets.unavailable")}
              </Alert>
            ) : (
              <>
                <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mt: 2.5 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {total ? formatMoney(total.amount, total.currency, locale) : "—"}
                  </Typography>
                  {total?.compareAt != null && total.compareAt > total.amount && (
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: 18, textDecoration: "line-through" }}
                    >
                      {formatMoney(total.compareAt, total.currency, locale)}
                    </Typography>
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t("sets.priceNote")}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <LocalShippingOutlinedIcon color="success" sx={{ fontSize: 18 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t("delivery")}
                  </Typography>
                </Stack>

                <Divider ref={optionsRef} sx={{ my: 3, scrollMarginTop: 16 }} />

                <Box
                  sx={partial ? { opacity: 0.5, pointerEvents: "none" } : undefined}
                  aria-disabled={partial ? true : undefined}
                >
                  <OptionDensityContext.Provider value={desktop}>
                    <Stack spacing={desktop ? 2.5 : 3} sx={{ counterReset: STEP_COUNTER }}>
                      <SetMaterialPicker
                        options={materialOptions}
                        material={finish.material}
                        onChange={(m) => handleFinishChange("material", m)}
                        axes={axes}
                        locale={locale}
                        tArt={t as unknown as Parameters<typeof SetMaterialPicker>[0]["tArt"]}
                        tEditor={tEditor as unknown as Parameters<typeof SetMaterialPicker>[0]["tEditor"]}
                      />
                      <FinishPicker
                        variants={finishVariants}
                        axes={axes}
                        finish={finish}
                        locale={locale}
                        onChange={handleFinishChange}
                        t={te}
                      />
                      <SizePicker
                        groups={groups}
                        selectedId={selected.variant.id}
                        currency={currency}
                        locale={locale}
                        axes={axes}
                        orientation={orientation}
                        onOrientationChange={() => undefined}
                        onSelect={(v: CanvasVariant) => !partial && setSelectedId(v.id)}
                        canRotate={false}
                        t={te}
                      />
                    </Stack>
                  </OptionDensityContext.Provider>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    {t("sets.sameForAll")}
                  </Typography>
                </Box>

                {partial ? (
                  <Alert
                    severity="warning"
                    sx={{ mt: 3 }}
                    onClose={adding ? undefined : () => setPartial(null)}
                  >
                    <AlertTitle>{t("sets.partialTitle")}</AlertTitle>
                    {partial.added.length > 0 && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>{t("sets.addedLabel")}</strong>{" "}
                        {partial.added.map(titleOf).join(", ")}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <strong>{t("sets.failedLabel")}</strong>{" "}
                      {partial.failed
                        .map((f) => (f.message ? `${titleOf(f.slug)} — ${f.message}` : titleOf(f.slug)))
                        .join(", ")}
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        disabled={!!adding}
                        onClick={handleRetry}
                        startIcon={adding ? <CircularProgress size={14} color="inherit" /> : undefined}
                      >
                        {t("sets.retry")}
                      </Button>
                      {partial.added.length > 0 && (
                        <Button size="small" disabled={!!adding} onClick={openCartDrawer}>
                          {t("sets.openCart")}
                        </Button>
                      )}
                    </Stack>
                  </Alert>
                ) : (
                  <Button
                    ref={addRef}
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{ mt: 3, minHeight: 52, fontWeight: 700 }}
                    disabled={!!adding}
                    onClick={handleAdd}
                    startIcon={adding ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    {adding
                      ? t("sets.adding", { current: adding.current, total: adding.total })
                      : t("sets.addToCart")}
                  </Button>
                )}

                <Box sx={{ mt: 2 }}>
                  <TrustStrip lines={trustPointKeys(material, mount).map((key) => te(key))} />
                </Box>
              </>
            )}
          </Grid>
        </Grid>

        {pieces.length > 0 && (
          <Box component="section" sx={{ mt: { xs: 5, md: 7 } }}>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              {t("sets.piecesHeading")}
            </Typography>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {pieces.map((p, i) => (
                <Grid key={p.id} size={{ xs: 12, sm: 4 }}>
                  <SetPieceRow
                    artwork={p}
                    href={artworkPath(localeSegment, p)}
                    locale={locale}
                    size={
                      selected
                        ? sizeLabel(selected.pieces[i].widthMm, selected.pieces[i].heightMm)
                        : null
                    }
                    price={
                      piecePrice ? formatMoney(piecePrice.amount, piecePrice.currency, locale) : null
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Link component={NextLink} href={root} sx={{ display: "inline-block", my: 4 }}>
          ← {t("backToGallery")}
        </Link>
      </Container>

      <MobileContinueBar
        targetRef={optionsRef}
        actionRef={addRef}
        price={total ? formatMoney(total.amount, total.currency, locale) : null}
      />
    </>
  );
}

function SetPieceRow({
  artwork,
  href,
  locale,
  size,
  price,
}: {
  artwork: Artwork;
  href: string;
  locale: string;
  size: string | null;
  price: string | null;
}) {
  const title = localized(artwork.title, locale);
  return (
    <Box
      component={NextLink}
      href={href}
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "center",
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        color: "inherit",
        textDecoration: "none",
        height: "100%",
        "&:hover": { borderColor: "text.secondary" },
        "&:focus-visible": { outline: "3px solid", outlineColor: "primary.main", outlineOffset: 2 },
      }}
    >
      <Box
        component="img"
        src={artworkImageUrl(artwork.slug, "thumb", artwork.updatedAt)}
        alt=""
        loading="lazy"
        sx={{
          width: 72,
          height: 72,
          objectFit: "contain",
          flexShrink: 0,
          borderRadius: 1,
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ letterSpacing: ".07em", textTransform: "uppercase", display: "block" }}
        >
          {artwork.artist}
        </Typography>
        <Typography sx={{ lineHeight: 1.3, fontWeight: 600 }}>
          {title}
          {artwork.year ? (
            <Typography component="span" color="text.secondary">{`, ${artwork.year}`}</Typography>
          ) : null}
        </Typography>
        {(size || price) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {[size, price].filter(Boolean).join(" · ")}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
